/**
 * MaintenanceGate — what people see while the platform is in maintenance.
 *
 * ⚠️ e2e 2026-09-24: with maintenance on, the customer was accepted at /login
 * (the endpoint is exempt so staff can get in), then every call answered 503
 * and the app treated it as a dead session: back to /login, forever. The
 * message the super admin wrote was never shown anywhere.
 *
 *  - signed-in customer → a full-screen notice with that message and the
 *    expected end, checking again on its own until maintenance is over;
 *  - visitor (login, landing…) → a banner above the page, so staff can still
 *    sign in;
 *  - super admin → a thin reminder that customers are locked out.
 *
 * Learns about maintenance from the public status endpoint (on mount and every
 * 30 s while active) and from any 503 MAINTENANCE_MODE the HTTP layer sees.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react'

import { useTranslation } from '@platform/i18n/hooks/useTranslation'
import { useQueryClient } from '@tanstack/react-query'

import { apiService } from '@/platform/api/api.service'
import { useAuth } from '@/shared/contexts/AuthContext'
import { userConfigService } from '@/shared/services/user-config.service'

import {
  subscribeToMaintenance,
  subscribeToMaintenanceRecheck,
  type MaintenanceNotice,
} from './maintenance-events'

export const PUBLIC_MAINTENANCE_STATUS_ENDPOINT = '/api/maintenance/status'
const MAINTENANCE_RECHECK_MS = 30_000

interface PublicMaintenanceStatus {
  enabled: boolean
  message: string
  scheduledEnd: string | null
}

const formatWhen = (iso: string): string =>
  `${userConfigService.formatDate(iso)} ${userConfigService.formatTime(iso)}`

export const MaintenanceGate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { t } = useTranslation('common')
  const { isAuthenticated, isSuperAdmin } = useAuth()
  const queryClient = useQueryClient()
  const [notice, setNotice] = useState<MaintenanceNotice | null>(null)
  const noticeRef = useRef<MaintenanceNotice | null>(null)
  noticeRef.current = notice

  const check = useCallback(async () => {
    try {
      const response = await apiService.get<PublicMaintenanceStatus>(
        PUBLIC_MAINTENANCE_STATUS_ENDPOINT,
        undefined,
        true,
      )
      const status = response.data
      if (status?.enabled) {
        setNotice({ message: status.message, scheduledEnd: status.scheduledEnd })
      } else if (noticeRef.current) {
        // Over: drop the notice and refetch everything that 503'd meanwhile.
        setNotice(null)
        void queryClient.invalidateQueries()
      }
    } catch {
      // Unreachable status: keep whatever we know; the next tick retries.
    }
  }, [queryClient])

  useEffect(() => {
    void check()
  }, [check])

  useEffect(() => subscribeToMaintenance(setNotice), [])

  // The super admin's own switch: reflect it now, not on the next 30 s tick.
  useEffect(
    () =>
      subscribeToMaintenanceRecheck(() => {
        void check()
      }),
    [check],
  )

  useEffect(() => {
    if (!notice) return undefined
    const id = setInterval(() => {
      void check()
    }, MAINTENANCE_RECHECK_MS)
    return () => clearInterval(id)
  }, [notice, check])

  if (!notice) return <>{children}</>

  if (isSuperAdmin) {
    return (
      <>
        <div
          role="status"
          data-testid="maintenance-staff-banner"
          className="bg-yellow-500/15 border-b border-yellow-500/40 px-4 py-2 text-center text-sm text-yellow-300 [html[data-theme=light]_&]:text-yellow-900"
        >
          {t('maintenance.staffBanner')}
        </div>
        {children}
      </>
    )
  }

  if (!isAuthenticated) {
    return (
      <>
        <div
          role="status"
          data-testid="maintenance-visitor-banner"
          className="bg-yellow-500/15 border-b border-yellow-500/40 px-4 py-3 text-center text-sm text-crypto-text"
        >
          <strong>{t('maintenance.title')}</strong>
          {notice.message ? ` — ${notice.message}` : null}
          {notice.scheduledEnd
            ? ` ${t('maintenance.backAt', { date: formatWhen(notice.scheduledEnd) })}`
            : null}
        </div>
        {children}
      </>
    )
  }

  return (
    <main
      data-testid="maintenance-notice"
      className="min-h-screen flex items-center justify-center px-4 py-12 bg-crypto-dark text-crypto-text"
    >
      <div className="max-w-xl text-center">
        <h1 className="text-2xl font-bold mb-4">{t('maintenance.title')}</h1>
        {notice.message ? (
          <p className="text-lg mb-4" data-testid="maintenance-notice-message">
            {notice.message}
          </p>
        ) : null}
        {notice.scheduledEnd ? (
          <p className="text-sm text-crypto-muted mb-4" data-testid="maintenance-notice-end">
            {t('maintenance.backAt', { date: formatWhen(notice.scheduledEnd) })}
          </p>
        ) : null}
        <p className="text-sm text-crypto-muted mb-6">{t('maintenance.autoRecheck')}</p>
        <button
          type="button"
          onClick={() => {
            void check()
          }}
          className="px-4 py-2 rounded-lg bg-crypto-accent text-white hover:opacity-90"
        >
          {t('maintenance.checkAgain')}
        </button>
      </div>
    </main>
  )
}
